import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface LotteryItem {
  id: string;
  content: string;
  sort_order: number;
  created_at: number;
}

export function useLottery() {
  const [items, setItems] = useState<LotteryItem[]>([]);
  const [loading, setLoading] = useState(true);
  // 本地操作時暫時抑制 realtime 回寫，避免閃爍
  const suppressRef = useRef(false);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from('lottery_items')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (data) {
      setItems(data.map(r => ({
        id: r.id,
        content: r.content,
        sort_order: r.sort_order ?? 0,
        created_at: new Date(r.created_at).getTime(),
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();

    const channel = supabase
      .channel('lottery_items_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lottery_items' }, () => {
        if (suppressRef.current) return;
        fetchItems();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchItems]);

  const addItem = useCallback(async (content: string) => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    const tempId = `temp-${Date.now()}`;
    const tempItem: LotteryItem = {
      id: tempId,
      content: content.trim(),
      sort_order: maxOrder,
      created_at: Date.now(),
    };

    // 樂觀更新：立即加入畫面
    setItems(prev => [...prev, tempItem]);

    suppressRef.current = true;
    const { data } = await supabase
      .from('lottery_items')
      .insert({ content: content.trim(), sort_order: maxOrder })
      .select()
      .single();

    // 用真實 id 替換暫時 id
    if (data) {
      setItems(prev => prev.map(item =>
        item.id === tempId
          ? { id: data.id, content: data.content, sort_order: data.sort_order ?? 0, created_at: new Date(data.created_at).getTime() }
          : item
      ));
    }
    setTimeout(() => { suppressRef.current = false; }, 500);
  }, [items]);

  const updateItem = useCallback(async (id: string, content: string) => {
    // 樂觀更新：立即更新畫面
    setItems(prev => prev.map(item => item.id === id ? { ...item, content: content.trim() } : item));

    suppressRef.current = true;
    await supabase.from('lottery_items').update({ content: content.trim() }).eq('id', id);
    setTimeout(() => { suppressRef.current = false; }, 500);
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    // 樂觀更新：立即從畫面移除
    setItems(prev => prev.filter(item => item.id !== id));

    suppressRef.current = true;
    await supabase.from('lottery_items').delete().eq('id', id);
    setTimeout(() => { suppressRef.current = false; }, 500);
  }, []);

  const drawRandom = useCallback((): LotteryItem | null => {
    if (items.length === 0) return null;
    const idx = Math.floor(Math.random() * items.length);
    return items[idx];
  }, [items]);

  return { items, loading, addItem, updateItem, deleteItem, drawRandom };
}
